import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Lightweight replacement for framer-motion's useInView using native IntersectionObserver.
 * Avoids forced reflows (getBoundingClientRect) that cause TBT spikes.
 */
export function useInView(
  ref: RefObject<Element | null>,
  options?: { once?: boolean; margin?: string }
): boolean {
  const [inView, setInView] = useState(false);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (options?.once && hasTriggered.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          hasTriggered.current = true;
          if (options?.once) observer.disconnect();
        } else if (!options?.once) {
          setInView(false);
        }
      },
      { rootMargin: options?.margin || "0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, options?.once, options?.margin]);

  return inView;
}
